import React, { useEffect, useState } from "react";
import { Box, Button, Chip, Grid, Paper, Typography, styled, Divider, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { DatePicker } from '@mui/x-date-pickers';
import { useFormik } from "formik";
import { toast } from "sonner";
import { api } from "../api";
import { fetchToken } from "../auth/Token";
import Header from "../components/Header";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import PaidIcon from '@mui/icons-material/Paid';
import ReceiptRoundedIcon from '@mui/icons-material/ReceiptRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import MoneyOffCsredRoundedIcon from '@mui/icons-material/MoneyOffCsredRounded';
import CreditScoreRoundedIcon from '@mui/icons-material/CreditScoreRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';

// Styled Components
const DashboardCard = styled(Paper)(({ theme }) => ({
  backgroundColor: '#fff',
  padding: theme.spacing(3),
  borderRadius: '12px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)'
  }
}));

const StatCard = styled(Paper)(({ theme, color }) => ({
  backgroundColor: color || theme.palette.primary.main,
  color: '#fff',
  padding: theme.spacing(2),
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: '100px'
}));

const LoanDashboard = ({ society_id, account_id }) => {
  const [loanReport, setLoanReport] = useState(null);
  const [loanSchedule, setLoanSchedule] = useState(null);
  const [period, setPeriod] = useState("Quarterly");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState("");
  const [clusters, setClusters] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch hierarchical data
  const fetchClusters = () => {
    api.get(`/association/get/clusters/${society_id}`, {
      headers: { Authorization: "Bearer " + fetchToken() }
    })
    .then(response => setClusters(response.data?.all))
    .catch(console.error);
  };

  const fetchCommunities = () => {
    if (!selectedCluster?.id) return;
    api.get(`/association/get/communities/in/cluster/${selectedCluster.id}`, {
      headers: { Authorization: "Bearer " + fetchToken() }
    })
    .then(response => setCommunities(response.data?.communities))
    .catch(console.error);
  };

  const fetchGroups = () => {
    if (!selectedCommunity?.id) return;
    api.get(`/association/get/groups/in/communities/${selectedCommunity.id}`, {
      headers: { Authorization: "Bearer " + fetchToken() }
    })
    .then(response => setGroups(response.data?.groups))
    .catch(console.error);
  };

  // Fetch loan data
  const fetchLoanData = async () => {
    setLoading(true);
    const data = new FormData();
    data.append("society_id", society_id);
    data.append("periodic_state", period);
    data.append("selected_year", selectedYear);
    data.append("selected_month", selectedMonth);
    data.append("cluster_id", selectedCluster?.id || 0);
    data.append("community_id", selectedCommunity?.id || 0);
    data.append("group_id", selectedGroup?.id || 0);
    if (account_id) {
      data.append("loan_account_id", account_id);
      data.append("account_id", account_id);
    }

    try {
      const [portfolioRes, scheduleRes] = await Promise.all([
        api.post("/dashboards/get/loan/portfolio", data, {
          headers: { Authorization: "Bearer " + fetchToken() }
        }),
        api.post("/dashboards/loan/schedule/report", data, {
          headers: { Authorization: "Bearer " + fetchToken() }
        })
      ]);
      
      setLoanReport(portfolioRes.data);
      setLoanSchedule(scheduleRes.data);
      toast.success("Loan data loaded successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to load loan data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClusters();
  }, [society_id]);

  useEffect(() => {
    fetchCommunities();
  }, [selectedCluster]);

  useEffect(() => {
    fetchGroups();
  }, [selectedCommunity]);

  useEffect(() => {
    fetchLoanData();
  }, [society_id, period, selectedYear, selectedMonth, selectedCluster, selectedCommunity, selectedGroup, account_id]);

  // Data processing functions
  const getPerformanceTrend = () => {
    if (!loanReport?.periods) return [];
    return loanReport.periods.map(period => ({
      name: period.name,
      disbursed: period.metrics.loans_disbursed.amount,
      repaid: period.metrics.loans_repaid.amount,
      outstanding: period.metrics.outstanding_loans.amount
    }));
  };

  const getPortfolioDistribution = () => {
    if (!loanReport?.summary) return [];
    return [
      { name: 'Disbursed', value: loanReport.summary.total_disbursed },
      { name: 'Repaid', value: loanReport.summary.total_repaid },
      { name: 'Outstanding', value: loanReport.summary.total_outstanding }
    ];
  };

  const getDelinquencyData = () => {
    if (!loanSchedule?.summary) return [];
    return [
      { name: 'On Time', value: loanSchedule.summary.total_repayment_paid },
      { name: 'Overdue', value: loanSchedule.summary.total_repayment_overdue },
      { name: 'Pending', value: loanSchedule.summary.total_repayment_due }
    ];
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  // Summary statistics card component
  const StatisticCard = ({ title, value, icon, trend, trendValue }) => (
    <StatCard color="#2E3B55">
      <Box>
        <Typography variant="subtitle2" fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
          <b>{title}</b>
        </Typography>
        <Typography variant="h5" fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
          <b>{new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(value)}</b>
        </Typography>
        {/* {trend && (
          <Box display="flex" alignItems="center" mt={1}>
            {trend === 'up' ? (
              <ArrowDropUpRoundedIcon sx={{ color: '#4CAF50' }} />
            ) : (
              <ArrowDropDownRoundedIcon sx={{ color: '#F44336' }} />
            )}
            <Typography variant="caption" color={trend === 'up' ? '#4CAF50' : '#F44336'}>
              <b>{trendValue}%</b>
            </Typography>
          </Box>
        )} */}
      </Box>
      {icon}
    </StatCard>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
        <b>Loan Portfolio Dashboard</b>
      </Typography>
      
      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: '12px' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel><b>Period</b></InputLabel>
              <Select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                label="Period"
              >
                <MenuItem value="Yearly"><b>Yearly</b></MenuItem>
                <MenuItem value="Quarterly"><b>Quarterly</b></MenuItem>
                <MenuItem value="Monthly"><b>Monthly</b></MenuItem>
                <MenuItem value="Weekly"><b>Weekly</b></MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel><b>Year</b></InputLabel>
              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                label="Year"
              >
                {[2023, 2024, 2025].map(year => (
                  <MenuItem key={year} value={year}><b>{year}</b></MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {period === "Monthly" || period === "Weekly" ? (
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel><b>Month</b></InputLabel>
                <Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  label="Month"
                >
                  {[
                    "January", "February", "March", "April", "May", "June",
                    "July", "August", "September", "October", "November", "December"
                  ].map(month => (
                    <MenuItem key={month} value={month}><b>{month}</b></MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ) : null}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ my:1 }}>
              <InputLabel><b>Cluster</b></InputLabel>
              <Select
                value={selectedCluster?.id || ''}
                onChange={(e) => setSelectedCluster(clusters.find(c => c.cluster_id === e.target.value))}
                label="Cluster"
              >
                <MenuItem value=""><b>All Clusters</b></MenuItem>
                {clusters.map(cluster => (
                  <MenuItem key={cluster.cluster_id} value={cluster.cluster_id}><b>{cluster.cluster_name}</b></MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ my:1 }}>
              <InputLabel><b>Communities</b></InputLabel>
              <Select
                value={selectedCommunity?.id || ''}
                onChange={(e) => selectedCommunity(communities.find(c => c.community_id === e.target.value))}
                label="Communities"
              >
                <MenuItem value=""><b>All Communities</b></MenuItem>
                {communities.map(c => (
                  <MenuItem key={c.community_id} value={c.community_id}><b>{c.community_name}</b></MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ my:1 }}>
              <InputLabel><b>Savings Groups</b></InputLabel>
              <Select
                value={selectedGroup?.id || ''}
                onChange={(e) => selectedGroup(groups.find(c => c.association_id === e.target.value))}
                label="Savings Groups"
              >
                <MenuItem value=""><b>All Groups</b></MenuItem>
                {groups.map(g => (
                  <MenuItem key={g.association_id} value={g.association_id}><b>{g.association_name}</b></MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button 
              variant="contained" 
              fullWidth 
              onClick={fetchLoanData}
              sx={{ height: '56px' }}
            >
              <b>Apply</b>
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <StatisticCard 
            title="Total Disbursed" 
            value={loanReport?.summary?.total_disbursed || 0} 
            icon={<PaidIcon sx={{ fontSize: 40 }} />}
            trend="up"
            trendValue="12.5"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatisticCard 
            title="Total Repaid" 
            value={loanReport?.summary?.total_repaid || 0} 
            icon={<ReceiptRoundedIcon sx={{ fontSize: 40 }} />}
            trend="up"
            trendValue="8.3"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatisticCard 
            title="Outstanding" 
            value={loanReport?.summary?.total_outstanding || 0} 
            icon={<MoneyOffCsredRoundedIcon sx={{ fontSize: 40 }} />}
            trend="down"
            trendValue="4.2"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatisticCard 
            title="Delinquent Loans" 
            value={loanSchedule?.summary?.total_repayment_overdue || 0} 
            icon={<CreditScoreRoundedIcon sx={{ fontSize: 40 }} />}
          />
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Performance Trend */}
        <Grid item xs={12} md={8}>
          <DashboardCard>
            <Typography variant="h6" gutterBottom fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
              <b>Loan Performance Trend</b>
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={getPerformanceTrend()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => [new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(value), 'Amount']} />
                  <Legend />
                  <Line type="monotone" dataKey="disbursed" stroke="#8884d8" activeDot={{ r: 8 }} />
                  <Line type="monotone" dataKey="repaid" stroke="#82ca9d" />
                  <Line type="monotone" dataKey="outstanding" stroke="#ffc658" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </DashboardCard>
        </Grid>

        {/* Portfolio Distribution */}
        <Grid item xs={12} md={4}>
          <DashboardCard>
            <Typography variant="h6" gutterBottom fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
              <b>Portfolio Distribution</b>
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={getPortfolioDistribution()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {getPortfolioDistribution().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </DashboardCard>
        </Grid>

        {/* Loan Disbursement by Period */}
        <Grid item xs={12} md={6}>
          <DashboardCard>
            <Typography variant="h6" gutterBottom fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
              <b>Loan Disbursement by {period}</b>
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={loanReport?.periods || []}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={70} />
                  <YAxis />
                  <Tooltip formatter={(value) => [new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(value), 'Amount']} />
                  <Legend />
                  <Bar dataKey="metrics.loans_disbursed.amount" name="Disbursed" fill="#8884d8" />
                  <Bar dataKey="metrics.loans_approved.amount" name="Approved" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </DashboardCard>
        </Grid>

        {/* Repayment Performance */}
        <Grid item xs={12} md={6}>
          <DashboardCard>
            <Typography variant="h6" gutterBottom fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
              <b>Repayment Performance</b>
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={loanReport?.periods || []}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={70} />
                  <YAxis />
                  <Tooltip formatter={(value) => [new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(value), 'Amount']} />
                  <Legend />
                  <Bar dataKey="metrics.loans_repaid.amount" name="Repaid" fill="#4CAF50" />
                  <Bar dataKey="metrics.outstanding_loans.amount" name="Outstanding" fill="#FF9800" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </DashboardCard>
        </Grid>

        {/* Delinquency Analysis */}
        <Grid item xs={12} md={6}>
          <DashboardCard>
            <Typography variant="h6" gutterBottom fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
              <b>Delinquency Analysis</b>
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={getDelinquencyData()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {getDelinquencyData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={['#4CAF50', '#F44336', '#2196F3'][index % 3]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </DashboardCard>
        </Grid>

        {/* Recent Disbursements */}
        <Grid item xs={12} md={6}>
          <DashboardCard>
            <Typography variant="h6" gutterBottom fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
              <b>Recent Loan Disbursements</b>
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 400, overflow: 'auto' }}>
              {loanReport?.periods?.slice(-1)[0]?.metrics?.loans_disbursed?.details?.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}><b>Member</b></th>
                      <th style={{ padding: '12px', textAlign: 'right' }}><b>Amount</b></th>
                      <th style={{ padding: '12px', textAlign: 'right' }}><b>Interest Rate</b></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loanReport.periods.slice(-1)[0].metrics.loans_disbursed.details
                      .slice(0, 8)
                      .map((loan, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '12px' }}><b>{loan.member_name}</b></td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>
                            <b>{new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(loan.amount)}</b>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>
                            <b>{loan.interest_rate ? `${loan.interest_rate}%` : 'N/A'}</b>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              ) : (
                <Typography variant="body1" textAlign="center" mt={5}>
                  <b>No recent disbursements</b>
                </Typography>
              )}
            </Box>
          </DashboardCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LoanDashboard;